import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Stethoscope, 
  Users, 
  Calendar, 
  FileText, 
  Shield, 
  Clock,
  Star,
  ArrowRight,
  Smile,
  MapPin,
  Phone,
  CheckCircle2,
  Mail,
  Youtube,
  Facebook,
  Instagram
} from 'lucide-react';

const LandingPage = () => {
  const features = [
    {
      icon: FileText,
      title: 'Seamless Clinical Documentation',
      description: 'Digital dental charts and automated clinical notes. Treatment histories, progress tracking, and diagnostics in one place. Easy access to records for patient visits and follow-ups. Designed for dental workflows to reduce paperwork and speed up care.'
    },
    {
      icon: Calendar,
      title: 'Smart Dental Scheduling',
      description: 'Intelligent appointment booking based on dentist availability. Online booking and automated SMS/email reminders. Chair-based scheduling to optimize clinic flow. Minimize no-shows and keep your schedule synchronized.'
    },
    {
      icon: Users,
      title: 'Role-Based Portals',
      description: 'Separate secure access for dentists, hygienists, admins, and patients. Patients can view records, appointments, and communications. Staff can manage clinical tasks and administrative duties efficiently.'
    },
    {
      icon: FileText,
      title: 'Digital Dental Forms & Imaging',
      description: 'Online intake forms, consent signatures, and health questionnaires. Imaging integrations — X-rays, intraoral photos, and charts in one record. Make the first visit, treatment planning, and documentation smoother.'
    },
    {
      icon: Stethoscope,
      title: 'Reporting & Analytics',
      description: 'Monitor clinic performance and patient outcomes. Insights into appointment trends, treatment plans, and resource use. Improve practice management decisions with real data.'
    }
  ];

  return (
    <>
        <Helmet>
        <title>Gugan Dental Care — Advanced Dental Practice with Integrated EHR Solution</title>
        <meta name="description" content="Delivering Exceptional Smiles Since 2017. Multi-specialty dental clinic in Gobichettipalayam with state-of-the-art Dental EHR & Practice Management System." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Navigation */}
        <nav className="fixed top-0 w-full z-50 glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 lg:py-0 lg:h-20">
              {/* Logo Section - Left */}
              <motion.div 
                className="flex items-center space-x-3 flex-shrink-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <a 
                  href="https://gugandentalcare.com" 
                  className="cursor-pointer"
                >
                  <img 
                    src="/logo.png" 
                    alt="Gugan Dental Care Logo" 
                    className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo not found
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        e.target.style.display = 'none';
                        fallback.style.display = 'block';
                      }
                    }}
                  />
                </a>
                <Stethoscope className="h-8 w-8 text-blue-400 hidden" />
              </motion.div>
              
              {/* Contact Info & Social Media Section - Right, Same Row on Mobile */}
              <motion.div 
                className="flex items-center gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm flex-shrink-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Phone */}
                <a 
                  href="tel:+919629996774" 
                  className="flex items-center gap-1 hover:text-blue-400 transition-colors text-gray-300"
                >
                  <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="whitespace-nowrap hidden sm:inline">+91 96299 96774</span>
                  <span className="whitespace-nowrap sm:hidden text-xs">+91 96299 96774</span>
                </a>
                
                {/* Email */}
                <a 
                  href="mailto:gugandentalcare@gmail.com" 
                  className="flex items-center gap-1 hover:text-blue-400 transition-colors text-gray-300"
                >
                  <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                  <span className="whitespace-nowrap hidden lg:inline">gugandentalcare@gmail.com</span>
                  <span className="whitespace-nowrap hidden sm:inline lg:hidden">Email</span>
                </a>

                {/* Social Media Icons */}
                <div className="flex items-center gap-2 sm:gap-3 ml-1 sm:ml-2">
                  <a 
                    href="https://www.youtube.com/@gugandentalcare" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube className="h-4 w-4 sm:h-5 sm:w-5" />
                  </a>
                  <a 
                    href="https://www.facebook.com/gugandentalcare" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-blue-500 transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-4 w-4 sm:h-5 sm:w-5" />
                  </a>
                  <a 
                    href="https://www.instagram.com/gugandentalcare" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-pink-500 transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
                  </a>
                  <a 
                    href="https://www.google.com/maps/place/Gugan+Dental+Care" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-yellow-500 transition-colors flex items-center gap-1"
                    aria-label="Google Reviews"
                  >
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 fill-yellow-500 text-yellow-500" />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="gradient-text">Gugan Dental Care</span>
                <br />
                <span className="text-3xl md:text-4xl font-normal text-gray-300">Advanced Dental Practice with Integrated EHR Solution</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
                Delivering Exceptional Smiles Since 2017
              </p>
              <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-4xl mx-auto">
                At Gugan Dental Care, your oral health and comfort are our highest priority. Our multi-specialty dental clinic in Gobichettipalayam — led by Dr. S.P. Natarajan and a team of seasoned specialists — now offers a state-of-the-art Dental EHR & Practice Management System designed to elevate both patient experience and clinical outcomes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login">
                  <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Doctor Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/patient/login">
                  <Button size="lg" className="patient-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Patient Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a 
                  href="https://gugandentalcare.com" 
                 
                >
                  <Button size="lg" variant="outline" className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white px-8 py-4 text-lg">
                    Visit Gugan Dental Care
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold pb-4 gradient-text">
                Transforming Dental Care with Intelligent EHR & Practice Management
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our comprehensive Dental Electronic Health Record (EHR) system empowers Gugan Dental Care clinicians and staff to deliver more efficient, coordinated, and personalized oral healthcare.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="glass-effect border-gray-700 card-hover h-full">
                    <CardHeader>
                      <feature.icon className="h-12 w-12 text-blue-400 mb-4" />
                      <CardTitle className="text-white">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-300">
                        {feature.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
                Why Gugan Dental Care?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                For over 8 years, we've been delivering modern dental solutions with patient-centric care and advanced technology. Now, our integrated EHR platform supports enhanced clinical accuracy, faster workflows, and more meaningful dentist-patient communication.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="glass-effect border-gray-700 card-hover h-full">
                  <CardHeader>
                    <Smile className="h-12 w-12 text-blue-400 mb-4" />
                    <CardTitle className="text-white">Comprehensive Dentistry Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300">
                      Endodontics, Orthodontics, Prosthodontics, Oral Surgery, Laser Dentistry, Implants, and more.
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="glass-effect border-gray-700 card-hover h-full">
                  <CardHeader>
                    <MapPin className="h-12 w-12 text-blue-400 mb-4" />
                    <CardTitle className="text-white">Conveniently Located</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300">
                      Near Bustand Signal, Gobichettipalayam. Easy access for all your dental care needs.
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.div
              className="glass-effect rounded-2xl p-12 border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-6 gradient-text text-center">
                Why Patients Love About Us
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  'Highly experienced and friendly dental specialists',
                  'Precise diagnosis with modern tools',
                  'Comfortable, caring treatment experience',
                  'Easy appointment management and follow-up care',
                  'Now backed by intelligent EHR technology for smoother visits and better outcomes',
                  'All designed to help you smile brighter and healthier'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                    <p className="text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
                Get Started with Gugan Dental EHR Today
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Experience a smarter way to manage your dental care and clinic operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link to="/login">
                  <Button size="lg" className="medical-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Doctor Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/patient/login">
                  <Button size="lg" className="patient-gradient text-white hover:opacity-90 px-8 py-4 text-lg">
                    Patient Login
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a 
                  href="https://gugandentalcare.com" 
                 
                >
                  <Button size="lg" variant="outline" className="border-green-400 text-green-400 hover:bg-green-400 hover:text-white px-8 py-4 text-lg">
                    Visit Gugan Dental Care
                  </Button>
                </a>
              </div>
              
              {/* Contact Information Section */}
              <div className="mt-12 space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-6 gradient-text">Contact</h3>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-gray-300 mb-8">
                    <a 
                      href="tel:+919629996774" 
                      className="flex items-center gap-2 hover:text-blue-400 transition-colors"
                    >
                      <Phone className="h-5 w-5 text-blue-400" />
                      <span>+91 96299 96774</span>
                    </a>
                    <a 
                      href="mailto:gugandentalcare@gmail.com" 
                      className="flex items-center gap-2 hover:text-blue-400 transition-colors"
                    >
                      <Mail className="h-5 w-5 text-blue-400" />
                      <span>gugandentalcare@gmail.com</span>
                    </a>
                  </div>
                </div>

                {/* Addresses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-300">
                  <div className="rounded-lg p-6 border border-gray-700 bg-gradient-to-br from-blue-600/20 via-cyan-600/20 to-teal-600/20 hover:from-purple-600/40 hover:via-pink-600/40 hover:to-rose-600/40 transition-all duration-300 backdrop-blur-sm">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-blue-400" />
                      Main Branch
                    </h4>
                    <p className="leading-relaxed">
                      GRK Complex, Bawa Medical Upstairs,<br />
                      Near Bustand Signal,<br />
                      Gobichettipalayam – 638452
                    </p>
                  </div>
                  <div className="rounded-lg p-6 border border-gray-700 bg-gradient-to-br from-blue-600/20 via-cyan-600/20 to-teal-600/20 hover:from-purple-600/40 hover:via-pink-600/40 hover:to-rose-600/40 transition-all duration-300 backdrop-blur-sm">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-blue-400" />
                      Other Location
                    </h4>
                    <p className="leading-relaxed">
                      Vairam Bricks Complex, Vasthu Nagar,<br />
                      Saravana Theatre Rd,<br />
                      Gobichettipalayam – 638476
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo.png" 
                alt="Gugan Dental Care Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  // Fallback to icon if logo not found
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    e.target.style.display = 'none';
                    fallback.style.display = 'block';
                  }
                }}
              />
              <Stethoscope className="h-6 w-6 text-blue-400 hidden" />
              <span className="text-lg font-semibold gradient-text">Gugan Dental Care</span>
            </div>
            <p className="text-gray-400">
              © 2025 Gugan Dental Care. All rights reserved. Developed and maintained by{' '}
              <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;